import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/main.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import prawnImg from '../assets/prawn.jpg';
import vegImg from '../assets/veg.jpg';
import muttonImg from '../assets/mutton.jpg';
import chickenImg from '../assets/chicken.jpg';
import fishImg from '../assets/fish.jpg';
import shrimpImg from '../assets/shrimp.jpg';
import keralafishcurryImg from '../assets/keralafishcurry.jpg';
import crabImg from '../assets/crab.jpg';
import chickenfryImg from '../assets/chickenfry.jpg';
import gulabjamunImg from '../assets/gulabjamun.jpg';
import gajarImg from '../assets/gajar.jpg';
import shahiImg from '../assets/shahi.jpg';
import mangolassiImg from '../assets/mangolassi.jpg';
import buttermilkImg from '../assets/buttermilk.jpg';
import cooldrinkImg from '../assets/cooldrink.jpg';

const menuItems = [
    {
        id: 'prawn-biryani',
        name: 'Prawn Biryani',
        description:
            'Succulent prawns marinated in royal spices, slow-cooked with fragrant basmati rice for a truly regal experience.',
        image: prawnImg,
        price: 399,
        tag: "Chef’s Special",
        highlight: "Seafood Delight",
    },
    {
        id: 'veg-biryani',
        name: 'Veg Biryani',
        description:
            'A medley of garden-fresh vegetables and aromatic herbs, layered with saffron rice. Perfect for vegetarians and flavor lovers.',
        image: vegImg,
        price: 249,
        tag: 'Vegetarian',
        highlight: 'Healthy & Wholesome',
    },
    {
        id: 'mutton-biryani',
        name: 'Mutton Biryani',
        description:
            'Tender mutton pieces, slow-cooked to perfection and layered with rich, spiced rice. A royal treat for meat lovers.',
        image: muttonImg,
        price: 449,
        tag: 'Most Popular',
        highlight: 'Rich & Aromatic',
    },
    {
        id: 'chicken-dum-biryani',
        name: 'Chicken Dum Biryani',
        description:
            'Juicy chicken, marinated overnight and cooked on dum with authentic spices. The king of all biryanis!',
        image: chickenImg,
        price: 299,
        tag: 'Signature',
        highlight: 'Classic Favorite',
    },
    {
        id: 'fish-fry',
        name: 'Fish Fry',
        description:
            'Crispy golden fish fry, marinated with house spices and shallow-fried for a perfect crunch. A seafood lover’s must-try!',
        image: fishImg,
        price: 229,
        tag: 'New',
        highlight: 'Crispy & Spicy',
    },
    {
        id: 'grilled-shrimp',
        name: 'Grilled Shrimp',
        description:
            'Juicy shrimp grilled to perfection, brushed with garlic butter and served with a hint of lemon. A smoky seafood treat!',
        image: shrimpImg,
        price: 349,
        tag: 'Seafood',
        highlight: 'Grilled Goodness',
    },
    {
        id: 'kerala-fish-curry',
        name: 'Kerala Fish Curry',
        description:
            'Traditional Kerala-style fish curry simmered in coconut milk and tangy spices. A coastal classic bursting with flavor.',
        image: keralafishcurryImg,
        price: 279,
        tag: 'Regional',
        highlight: 'Coconut & Spice',
    },
    {
        id: 'crab-roast',
        name: 'Crab Roast',
        description:
            'Fresh crab cooked in a spicy masala, roasted to perfection for a rich and unforgettable taste.',
        image: crabImg,
        price: 499,
        tag: 'Premium',
        highlight: 'Spicy & Rich',
    },
    {
        id: 'chicken-ghee-roast',
        name: 'Chicken Ghee Roast',
        description:
            'Tender chicken pieces roasted in aromatic ghee and fiery spices. A Mangalorean delicacy for spice lovers.',
        image: chickenfryImg,
        price: 319,
        tag: 'Special',
        highlight: 'Ghee & Spice',
    },
];

const dessertItems = [
    {
        id: 'gulab-jamun',
        name: 'Gulab Jamun',
        description:
            'Soft, melt-in-the-mouth milk dumplings soaked in rose-scented sugar syrup.',
        image: gulabjamunImg,
        price: 99,
        tag: 'Dessert',
        highlight: 'Classic Sweet',
    },
    {
        id: 'gajar-halwa',
        name: 'Gajar Halwa',
        description:
            'Traditional Indian carrot pudding slow-cooked with milk, ghee, and nuts.',
        image: gajarImg,
        price: 109,
        tag: 'Dessert',
        highlight: 'Winter Favorite',
    },
    {
        id: 'shahi-tukra',
        name: 'Shahi Tukra',
        description:
            'Rich Mughlai dessert of fried bread soaked in saffron milk and garnished with dry fruits.',
        image: shahiImg,
        price: 119,
        tag: 'Dessert',
        highlight: 'Royal Treat',
    },
];

const beverageItems = [
    {
        id: 'mango-lassi',
        name: 'Mango Lassi',
        description: 'Refreshing yogurt-based mango drink, sweet and cooling.',
        image: mangolassiImg,
        price: 79,
        tag: 'Beverage',
        highlight: 'Summer Favorite',
    },
    {
        id: 'buttermilk',
        name: 'Buttermilk',
        description: 'Traditional spiced buttermilk, cool and soothing.',
        image: buttermilkImg,
        price: 59,
        tag: 'Beverage',
        highlight: 'Digestive',
    },
    {
        id: 'cool-drink',
        name: 'Cool Drink',
        description: 'Assorted chilled soft drinks to refresh your mood.',
        image: cooldrinkImg,
        price: 49,
        tag: 'Beverage',
        highlight: 'Chilled & Fizzy',
    },
];

const containerVariants = {
    visible: {
        transition: {
            staggerChildren: 0.18,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, scale: 0.85, y: 60 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            duration: 0.6,
            type: 'spring',
            bounce: 0.38,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        y: 40,
        transition: { duration: 0.25 },
    },
    hover: {
        scale: 1.045,
        boxShadow: '0 8px 32px #ffd70044, 0 2px 16px #fff4',
        transition: { duration: 0.18 },
    },
};

const imgVariants = {
    initial: { scale: 1.08, opacity: 0 },
    animate: {
        scale: 1,
        opacity: 1,
        transition: { duration: 0.7, ease: 'easeOut' },
    },
    hover: { scale: 1.04, transition: { duration: 0.2 } },
};

const descVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
        opacity: 1,
        y: 0,
        transition: { delay: 0.2, duration: 0.5 },
    },
};

const Menu = () => {
    const [selectedQty, setSelectedQty] = useState({});
    const [added, setAdded] = useState({});
    const { cart, addToCart, clearCart } = useCart();
    const navigate = useNavigate();

    // Calculate cart summary safely
    const totalItems = Array.isArray(cart) && cart.length > 0
        ? cart.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : 0;
    const totalPrice = Array.isArray(cart) && cart.length > 0
        ? cart.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0)
        : 0;

    // Helper to get unique key for each item (id is best)
    const getKey = (item) => item.id;

    const handleQtyChange = (item, newQty) => {
        if (newQty < 1) return;
        setSelectedQty({ ...selectedQty, [getKey(item)]: newQty });
    };

    const handleAddToCart = (item) => {
        const key = getKey(item);
        const qty = selectedQty[key] || 1;
        addToCart(item, qty);
        setAdded((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => setAdded((prev) => ({ ...prev, [key]: false })), 1200);
    };

    const renderSection = (title, items) => (
        <>
            <motion.h2
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                style={{
                    color: '#03045e',
                    textAlign: 'center',
                    marginBottom: '2.5rem',
                    fontWeight: 800,
                    letterSpacing: '0.03em',
                    fontSize: '2.2rem',
                    textShadow: '0 2px 8px #ffd70022',
                }}
            >
                {title}
            </motion.h2>
            <motion.div
                className="menu-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '2.5rem',
                    maxWidth: 1100,
                    margin: '0 auto',
                }}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <AnimatePresence>
                    {items.map((item) => {
                        const key = getKey(item);
                        const qty = selectedQty[key] || 1;
                        const totalPrice = qty * item.price;

                        return (
                            <motion.div
                                key={key}
                                variants={itemVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                whileHover="hover"
                                className="menu-card"
                                style={{
                                    background: 'rgba(255,255,255,0.55)',
                                    color: '#22223b',
                                    padding: '2.2rem 1.5rem 1.5rem 1.5rem',
                                    borderRadius: '2.5rem',
                                    boxShadow: '0 8px 32px #ffd70022, 0 2px 16px #fff4',
                                    textAlign: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '1.5px solid #f1f5f9',
                                    transition: 'box-shadow 0.2s, background 0.2s',
                                    backdropFilter: 'blur(8px)',
                                    minHeight: 500,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* Tag/Highlight */}
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1, duration: 0.4 }}
                                    style={{
                                        position: 'absolute',
                                        top: 24,
                                        left: 24,
                                        background: '#ffd700',
                                        color: '#001d4a',
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        borderRadius: '1rem',
                                        padding: '0.25em 1em',
                                        boxShadow: '0 2px 8px #ffd70033',
                                        zIndex: 2,
                                    }}
                                >
                                    {item.tag}
                                </motion.div>
                                {/* Highlight badge */}
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 0.85, y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.4 }}
                                    style={{
                                        position: 'absolute',
                                        top: 24,
                                        right: 24,
                                        background: '#2563eb',
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: '0.93rem',
                                        borderRadius: '1rem',
                                        padding: '0.22em 0.9em',
                                        boxShadow: '0 2px 8px #2563eb22',
                                        zIndex: 2,
                                        opacity: 0.85,
                                    }}
                                >
                                    {item.highlight}
                                </motion.div>
                                {/* Image */}
                                <motion.img
                                    src={item.image}
                                    alt={item.name}
                                    variants={imgVariants}
                                    initial="initial"
                                    animate="animate"
                                    whileHover="hover"
                                    style={{
                                        width: '100%',
                                        height: '180px',
                                        objectFit: 'cover',
                                        borderRadius: '1.5rem',
                                        boxShadow: '0 2px 12px #ffd70022',
                                    }}
                                />
                                {/* Name */}
                                <motion.h3
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15, duration: 0.5 }}
                                    style={{
                                        color: '#ffd700',
                                        marginTop: '1.2rem',
                                        fontWeight: 700,
                                        fontSize: '1.35rem',
                                        letterSpacing: '0.01em',
                                    }}
                                >
                                    {item.name}
                                </motion.h3>
                                {/* Description */}
                                <motion.p
                                    variants={descVariants}
                                    initial="initial"
                                    animate="animate"
                                    style={{
                                        color: '#64748b',
                                        fontSize: '1.08rem',
                                        margin: '0.7rem 0 1.1rem 0',
                                        minHeight: 48,
                                        fontWeight: 500,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {item.description}
                                </motion.p>
                                {/* Price */}
                                <motion.p
                                    style={{
                                        fontWeight: 'bold',
                                        margin: '0.7rem 0',
                                        color: '#22223b',
                                        fontSize: '1.07rem',
                                    }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                >
                                    ₹{item.price} × {qty} ={' '}
                                    <span style={{ color: '#2563eb' }}>₹{totalPrice}</span>
                                </motion.p>

                                {/* Quantity Stepper */}
                                <div
                                    style={{
                                        marginTop: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.7rem',
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => handleQtyChange(item, qty - 1)}
                                        style={{
                                            background: '#fff',
                                            border: '1.5px solid #bbb',
                                            borderRadius: '12px',
                                            width: '38px',
                                            height: '38px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: qty === 1 ? 'not-allowed' : 'pointer',
                                            opacity: qty === 1 ? 0.5 : 1,
                                            fontSize: '1.3rem',
                                            fontWeight: 700,
                                            color: '#222',
                                            transition: 'background 0.2s, border 0.2s, opacity 0.2s',
                                            backgroundClip: 'padding-box',
                                        }}
                                        disabled={qty === 1}
                                        aria-label="Decrease quantity"
                                    >
                                        –
                                    </button>
                                    <span
                                        style={{
                                            minWidth: '2.2em',
                                            display: 'inline-block',
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            background: '#f3f4f6',
                                            color: '#222',
                                            borderRadius: '12px',
                                            padding: '0.2em 0.8em',
                                            textAlign: 'center',
                                            border: '1px solid #e5e7eb',
                                        }}
                                    >
                                        {qty}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleQtyChange(item, qty + 1)}
                                        style={{
                                            background: '#fff',
                                            border: '1.5px solid #bbb',
                                            borderRadius: '12px',
                                            width: '38px',
                                            height: '38px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            fontSize: '1.3rem',
                                            fontWeight: 700,
                                            color: '#222',
                                            transition: 'background 0.2s, border 0.2s',
                                            backgroundClip: 'padding-box',
                                        }}
                                        aria-label="Increase quantity"
                                    >
                                        +
                                    </button>
                                </div>

                                <motion.button
                                    className="primary-btn"
                                    style={{
                                        marginTop: '1.5rem',
                                        fontWeight: 'bold',
                                        letterSpacing: '0.03em',
                                        background: added[key] ? '#22c55e' : '#2563eb',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '1.5rem',
                                        padding: '0.8em 1.6em',
                                        fontSize: '1.08rem',
                                        boxShadow: '0 2px 12px #2563eb33',
                                        transition: 'background 0.2s, box-shadow 0.2s',
                                        outline: 'none',
                                    }}
                                    whileHover={{
                                        scale: 1.07,
                                        boxShadow: '0 0 32px #2563eb55',
                                        background: added[key] ? '#16a34a' : '#1e40af',
                                    }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => handleAddToCart(item)}
                                    disabled={added[key]}
                                >
                                    {added[key] ? 'Added!' : 'Add to Cart'}
                                </motion.button>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </motion.div>
        </>
    );

    return (
        <div
            style={{
                minHeight: '100vh',
                background:
                    'linear-gradient(135deg, #fffbe7cc 0%, #e0e7ffcc 100%)',
                fontFamily: "'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif",
                padding: '3rem 0.5rem 6rem 0.5rem',
                backdropFilter: 'blur(2px)',
            }}
        >
            {renderSection('Our Royal Menu', menuItems)}
            {renderSection('Desserts', dessertItems)}
            {renderSection('Some Drinks', beverageItems)}

            {/* Enhanced Proceed to Checkout Bar with Cart Summary */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    background: 'rgba(255, 215, 0, 0.97)',
                    boxShadow: '0 -2px 24px #ffd70055',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 200,
                    padding: '1.2rem 0',
                }}
            >
                <button
                    className="primary-btn large"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.2rem',
                        background: totalItems > 0 ? '#2563eb' : '#bfc9e0',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '1.22rem',
                        borderRadius: '2.5rem',
                        boxShadow: '0 2px 24px #2563eb55',
                        padding: '1.1rem 3.5rem',
                        border: 'none',
                        transition: 'background 0.2s, box-shadow 0.2s',
                        cursor: totalItems > 0 ? 'pointer' : 'not-allowed',
                        outline: 'none',
                        opacity: totalItems > 0 ? 1 : 0.7,
                    }}
                    onClick={() => totalItems > 0 && navigate('/checkout')}
                    aria-label="Proceed to Checkout"
                    disabled={totalItems === 0}
                >
                    <span style={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center' }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24">
                            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2zM7.16 14l.84-2h7.45c.75 0 1.41-.41 1.75-1.03l3.24-5.88A1 1 0 0 0 19.45 4H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12z" fill="#fff"/>
                        </svg>
                    </span>
                    <span>
                        <strong>Proceed to Checkout</strong>
                        <span style={{
                            marginLeft: '1.1rem',
                            background: '#fff',
                            color: '#2563eb',
                            borderRadius: '1.2rem',
                            padding: '0.3rem 1.1rem',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: '0 2px 8px #2563eb33',
                        }}>
                            {totalItems > 0 ? `${totalItems} item${totalItems > 1 ? 's' : ''}` : 'No items'}
                        </span>
                    </span>
                    <span style={{
                        marginLeft: '1.2rem',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: '#ffd700',
                        background: '#22223b',
                        borderRadius: '1rem',
                        padding: '0.3rem 1.1rem',
                    }}>
                        {totalItems > 0 ? `₹${totalPrice}` : '₹0'}
                    </span>
                </button>
                {/* Cart Summary Description */}
                <span
                    style={{
                        marginLeft: '2rem',
                        color: '#22223b',
                        fontWeight: 500,
                        fontSize: '1.08rem',
                        background: '#fffbe7',
                        borderRadius: '1.2rem',
                        padding: '0.7rem 1.5rem',
                        boxShadow: '0 2px 8px #ffd70022',
                        maxWidth: 340,
                        textAlign: 'left',
                    }}
                >
                    {totalItems > 0 ? (
                        <>
                            <span style={{ color: '#2563eb', fontWeight: 700 }}>
                                {totalItems} {totalItems === 1 ? 'item' : 'items'}
                            </span>{" "}
                            in your cart.<br />
                            <span style={{ color: '#bfa100', fontWeight: 700 }}>
                                Total: ₹{totalPrice}
                            </span>
                            <br />
                            <span style={{ color: '#64748b', fontSize: '0.98rem' }}>
                                Review your royal selection before checkout!
                            </span>
                        </>
                    ) : (
                        <>
                            <span style={{ color: '#d90429', fontWeight: 700 }}>
                                Your cart is empty.
                            </span>
                            <br />
                            <span style={{ color: '#64748b', fontSize: '0.98rem' }}>
                                Add some delicious items to begin your royal feast!
                            </span>
                        </>
                    )}
                </span>
                {/* Empty Cart Button */}
                {totalItems > 0 && (
                    <motion.button
                        whileHover={{
                            scale: 1.09,
                            background: '#fff',
                            color: '#d90429',
                            border: '2px solid #d90429',
                        }}
                        whileTap={{ scale: 0.97 }}
                        style={{
                            marginLeft: '2rem',
                            background: '#fff',
                            color: '#d90429',
                            border: '2px solid #fff',
                            borderRadius: '2rem',
                            fontWeight: 700,
                            fontSize: '1.05rem',
                            padding: '0.7rem 1.7rem',
                            boxShadow: '0 2px 8px #ffd70033',
                            cursor: 'pointer',
                            transition: 'background 0.2s, color 0.2s, border 0.2s',
                        }}
                        onClick={() => {
                            if (window.confirm('Are you sure you want to empty your cart?')) {
                                clearCart();
                            }
                        }}
                        aria-label="Empty Cart"
                    >
                        🗑️ Empty Cart
                    </motion.button>
                )}
            </motion.div>
        </div>
    );
};

export default Menu;
